//TODO : Refactor after ai
/**
 * Interaktionsscreen „Baustelle heute“ (KI-Arbeitsplan Schritt 210), mobile first.
 *
 * Auf der Baustelle braucht der Gruppenführer für ein Ziel – die Maßnahme
 * fachgerecht ausführen und belegen – drei Objekte zugleich: den Einsatz
 * (Prüfwerte, Freigabe), die Rapportzeilen (ausgeführte Menge) und die
 * Materialbewegungen (Verbrauch). Die Detailseiten allein würden dafür drei
 * Wechsel verlangen; deshalb dieser eine Screen.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { personaVon, useDatenStore } from "@app/store";
import {
  Badge,
  Button,
  InputField,
  ObjectListCard,
} from "@shared/components/widgets/ooux";
import {
  alsListe,
  datensaetze,
  datensatz,
  neueId,
  objektKlasse,
  pruefeAusfuehrung,
  statusFarbe,
  type Datensatz,
} from "@shared/demo-data/viacid";
import { useTranslation } from "@shared/locales";
import { Auswahlfeld } from "./Auswahlfeld";

const AKTIV = ["Geplant", "Terminiert", "Ausführung freigegeben"];

export function BaustellePage() {
  const { t } = useTranslation();
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const persona = personaVon(rolle);
  const einsaetze = datensaetze(bestand, "einsatz")
    .filter((e) => e.status != null && AKTIV.includes(e.status))
    .filter(
      (e) =>
        !persona ||
        !["GF", "SU"].includes(rolle) ||
        alsListe(e.bezuege["einsatz-team"]).includes(persona) ||
        e.bezuege["einsatz-gefuehrt-von-mitarbeiter"] === persona
    )
    .sort((a, b) =>
      String(a.werte.einsatzdatum).localeCompare(String(b.werte.einsatzdatum))
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        maxWidth: 720,
      }}
    >
      <header>
        <h1
          style={{
            font: "var(--t-h6)",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          {t("viacid.baustelle.titel")}
        </h1>
        <p
          style={{
            font: "var(--t-paragraph-m)",
            color: "var(--text-tertiary)",
            margin: "4px 0 0",
          }}
        >
          {t("viacid.baustelle.einleitung")}
        </p>
      </header>
      {einsaetze.length === 0 && (
        <p style={{ color: "var(--text-tertiary)" }}>
          {t("viacid.baustelle.leer")}
        </p>
      )}
      {einsaetze.map((e) => (
        <EinsatzKarte key={e.id} einsatz={e} />
      ))}
    </div>
  );
}

function EinsatzKarte({ einsatz }: { einsatz: Datensatz }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const aendern = useDatenStore((s) => s.aendern);
  const anlegen = useDatenStore((s) => s.anlegen);
  const melden = useDatenStore((s) => s.melden);
  const klasse = objektKlasse("einsatz");
  const auftrag = datensatz(
    bestand,
    "auftrag",
    String(einsatz.bezuege["einsatz-fuer-auftrag"] ?? "")
  );
  const so = datensatz(
    bestand,
    "strassenobjekt",
    String(einsatz.bezuege["einsatz-am-strassenobjekt"] ?? "")
  );
  const istGF = rolle === "GF";
  const darfMelden = rolle === "GF" || rolle === "SU";

  const [boden, setBoden] = useState(
    String(einsatz.werte.bodentemperatur ?? "")
  );
  const [luft, setLuft] = useState(String(einsatz.werte.lufttemperatur ?? ""));
  const [feuchte, setFeuchte] = useState(
    String(einsatz.werte.luftfeuchtigkeit ?? "")
  );
  const [trocken, setTrocken] = useState(
    String(einsatz.werte.oberflaecheTrocken ?? "true")
  );
  const [menge, setMenge] = useState("");
  const [artikel, setArtikel] = useState("ar1");
  const [eimer, setEimer] = useState("");

  const zeit = () => new Date().toISOString().slice(0, 16);
  const pruefwerteSpeichern = () => {
    if (boden === "" || luft === "")
      return melden(t("viacid.baustelle.pruefwerteFehlen"), true);
    aendern(
      {
        slug: "einsatz",
        id: einsatz.id,
        werte: {
          bodentemperatur: Number(boden),
          lufttemperatur: Number(luft),
          luftfeuchtigkeit: Number(feuchte || 0),
          oberflaecheTrocken: trocken === "true",
          oberflaecheSauber: true,
          geprueftAm: zeit(),
        },
      },
      t("viacid.baustelle.pruefwerteGespeichert")
    );
  };
  const status = (ziel: string, begruendung?: string) => {
    if (ziel === "Ausführung freigegeben") {
      // Die eingetippten Werte zählen, auch wenn „Prüfwerte speichern“ nicht gedrückt wurde
      const werte = {
        ...einsatz.werte,
        ...(boden !== "" ? { bodentemperatur: Number(boden) } : {}),
        ...(luft !== "" ? { lufttemperatur: Number(luft) } : {}),
        oberflaecheTrocken: trocken === "true",
      };
      const probe = {
        ...bestand,
        einsatz: datensaetze(bestand, "einsatz").map((e) =>
          e.id === einsatz.id ? { ...e, werte } : e
        ),
      };
      const problem = pruefeAusfuehrung(probe, einsatz.id);
      if (problem) return melden(problem, true);
      if (boden !== "" && luft !== "") pruefwerteSpeichern();
    }
    aendern(
      {
        slug: "einsatz",
        id: einsatz.id,
        status: ziel,
        werte: begruendung
          ? { begruendungAusfuehrungsentscheidung: begruendung }
          : {},
      },
      `${t("viacid.baustelle.einsatz")} – ${ziel}.`
    );
  };
  const mengeMelden = () => {
    if (!menge) return;
    anlegen(
      "rapportzeile",
      {
        id: neueId(bestand, "rapportzeile"),
        titel: `Ausgeführte Menge · ${menge} lfm`,
        untertitel: zeit().slice(0, 10),
        kennung: null,
        status: null,
        werte: {
          zeilenart: "Ausgeführte Menge",
          gemeldeteMenge: Number(menge),
        },
        bezuege: {
          "rapportzeile-zu-einsatz": einsatz.id,
          ...(personaVon(rolle)
            ? { "rapportzeile-von-mitarbeiter": personaVon(rolle) as string }
            : {}),
        },
      },
      t("viacid.baustelle.mengeGemeldet", { menge })
    );
    setMenge("");
  };
  const materialMelden = () => {
    if (!eimer) return;
    const a = datensatz(bestand, "artikel", artikel);
    anlegen(
      "materialbewegung",
      {
        id: neueId(bestand, "materialbewegung"),
        titel: `Verbrauch · ${eimer} ${String(a?.werte.gebindeeinheit ?? "Stk")} ${a?.titel.split(" ")[0] ?? ""}`,
        untertitel: zeit().replace("T", " "),
        kennung: null,
        status: "Gemeldet",
        werte: {
          bewegungsart: "Verbrauch",
          menge: Number(eimer),
          einheit: String(a?.werte.gebindeeinheit ?? "Stk"),
          zeitpunkt: zeit(),
        },
        bezuege: {
          "materialbewegung-betrifft-artikel": artikel,
          "materialbewegung-fuer-einsatz": einsatz.id,
          ...(auftrag ? { "materialbewegung-fuer-auftrag": auftrag.id } : {}),
          ...(personaVon(rolle)
            ? {
                "materialbewegung-gemeldet-von-mitarbeiter": personaVon(
                  rolle
                ) as string,
              }
            : {}),
        },
      },
      t("viacid.baustelle.materialGemeldet")
    );
    setEimer("");
  };

  const gemeldet = datensaetze(bestand, "rapportzeile")
    .filter(
      (z) =>
        z.bezuege["rapportzeile-zu-einsatz"] === einsatz.id &&
        z.werte.zeilenart === "Ausgeführte Menge"
    )
    .reduce((s, z) => s + Number(z.werte.gemeldeteMenge ?? 0), 0);

  return (
    <ObjectListCard
      title={`${String(einsatz.werte.zeitslot ?? "")} · ${String(
        einsatz.werte.einsatzdatum ?? ""
      )
        .split("-")
        .reverse()
        .join(".")}`}
      actions={
        klasse && einsatz.status ? (
          <Badge color={statusFarbe(klasse, einsatz.status)} dot>
            {einsatz.status}
          </Badge>
        ) : undefined
      }
    >
      <div
        style={{
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <button
          type="button"
          onClick={() =>
            navigate(PagesRoutes.objekte.show("einsatz", einsatz.id))
          }
          style={{
            textAlign: "left",
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
          }}
        >
          <strong
            style={{ font: "var(--t-subtitle)", color: "var(--text-primary)" }}
          >
            {auftrag?.titel ?? einsatz.titel}
          </strong>
          <div
            style={{
              font: "var(--t-paragraph-s)",
              color: "var(--text-tertiary)",
            }}
          >
            {so?.untertitel ?? ""}
          </div>
        </button>

        {einsatz.status === "Terminiert" && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12,
              }}
            >
              <InputField
                label={t("viacid.baustelle.boden")}
                type="number"
                inputMode="decimal"
                value={boden}
                onChange={(e) => setBoden(e.target.value)}
                suffix="°C"
                disabled={!istGF}
              />
              <InputField
                label={t("viacid.baustelle.luft")}
                type="number"
                inputMode="decimal"
                value={luft}
                onChange={(e) => setLuft(e.target.value)}
                suffix="°C"
                disabled={!istGF}
              />
              <InputField
                label={t("viacid.baustelle.feuchte")}
                type="number"
                inputMode="decimal"
                value={feuchte}
                onChange={(e) => setFeuchte(e.target.value)}
                suffix="%"
                disabled={!istGF}
              />
              <Auswahlfeld
                label={t("viacid.baustelle.trocken")}
                value={trocken}
                onChange={setTrocken}
                optionen={[
                  { value: "true", label: t("viacid.ja") },
                  { value: "false", label: t("viacid.nein") },
                ]}
              />
            </div>
            {istGF ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Button
                  size="lg"
                  variant="outline"
                  color="neutral"
                  onClick={pruefwerteSpeichern}
                >
                  {t("viacid.baustelle.pruefwerteSpeichern")}
                </Button>
                <Button
                  size="lg"
                  onClick={() => status("Ausführung freigegeben")}
                >
                  {t("viacid.baustelle.freigeben")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  color="error"
                  onClick={() =>
                    status(
                      "Ausführung abgesagt",
                      t("viacid.baustelle.absageGrund")
                    )
                  }
                >
                  {t("viacid.baustelle.absagen")}
                </Button>
              </div>
            ) : (
              <p
                style={{
                  font: "var(--t-paragraph-s)",
                  color: "var(--text-tertiary)",
                  margin: 0,
                }}
              >
                {t("viacid.baustelle.nurGF")}
              </p>
            )}
          </>
        )}

        {einsatz.status === "Ausführung freigegeben" && darfMelden && (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <InputField
                label={t("viacid.baustelle.menge", { summe: gemeldet })}
                type="number"
                inputMode="decimal"
                value={menge}
                onChange={(e) => setMenge(e.target.value)}
                suffix="lfm"
              />
              <Button size="lg" variant="light" onClick={mengeMelden}>
                {t("viacid.baustelle.mengeMelden")}
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Auswahlfeld
                label={t("viacid.baustelle.material")}
                value={artikel}
                onChange={setArtikel}
                optionen={datensaetze(bestand, "artikel")
                  .filter((a) => a.status === "Aktiv")
                  .map((a) => ({ value: a.id, label: a.titel }))}
              />
              <InputField
                label={t("viacid.baustelle.anzahl")}
                type="number"
                inputMode="numeric"
                value={eimer}
                onChange={(e) => setEimer(e.target.value)}
              />
              <Button size="lg" variant="light" onClick={materialMelden}>
                {t("viacid.baustelle.materialMelden")}
              </Button>
            </div>
            {istGF && (
              <Button size="lg" onClick={() => status("Ausgeführt")}>
                {t("viacid.baustelle.ausgefuehrt")}
              </Button>
            )}
          </>
        )}
        {einsatz.status === "Geplant" && (
          <p
            style={{
              font: "var(--t-paragraph-s)",
              color: "var(--text-tertiary)",
              margin: 0,
            }}
          >
            {t("viacid.baustelle.nochGeplant")}
          </p>
        )}
      </div>
    </ObjectListCard>
  );
}
